import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Upload, X, AlertTriangle, Info, FileText, Video, Image } from "lucide-react";
import { Creative, Location } from "../../types";
import DayBasedUpload from "../creative-upload/DayBasedUpload";

interface CustomMaterialUploadProps {
  locations: Location[];
  locationCreatives: Record<string, Creative[]>;
  onLocationFileChange: (locationName: string, e: React.ChangeEvent<HTMLInputElement>, day?: number) => void;
  onRemoveLocationCreative: (locationName: string, index: number) => void;
  onUpdateLocationCreativeDays?: (locationName: string, creativeIndex: number, days: number[]) => void;
  isUploading: boolean;
  uploadProgress?: number;
}

const CustomMaterialUpload = ({
  locations,
  locationCreatives,
  onLocationFileChange,
  onRemoveLocationCreative,
  onUpdateLocationCreativeDays,
  isUploading,
  uploadProgress = 0
}: CustomMaterialUploadProps) => {
  const { language } = useLanguage();

  // Get detailed requirements based on screen dimensions
  const getDetailedRequirements = (screenDimensions: string) => {
    const baseRequirements = {
      videoFormat: "MP4 H264 @30FPS",
      imageFormat: "JPG in RGB Mode",
      maxFileSize: {
        image: "10MB",
        video: "100MB"
      },
      aspectRatio: "",
      screenResolution: "",
      instructions: "",
      technicalNotes: []
    };

    switch (screenDimensions) {
      case "10m x 16m":
        return {
          ...baseRequirements,
          screenResolution: "960 × 1536 pixels",
          aspectRatio: "10:16 (Portrait)",
          instructions: language === 'ar' 
            ? "يجب أن تكون المواد بتنسيق عمودي مع نسبة عرض إلى ارتفاع 10:16. تأكد من أن النص والعناصر المهمة واضحة ومقروءة من مسافة بعيدة."
            : "Materials must be in portrait format with 10:16 aspect ratio. Ensure text and important elements are clear and readable from a distance.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 960 × 1536 بكسل" : "Required resolution: 960 × 1536 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 10:16 (عمودي)" : "Aspect ratio: 10:16 (Portrait)",
            language === 'ar' ? "ضغط الفيديو: H.264 بمعدل 30 إطار في الثانية" : "Video compression: H.264 at 30fps",
            language === 'ar' ? "وضع الألوان للصور: RGB" : "Image color mode: RGB"
          ]
        };
      case "25m x 17m":
        return {
          ...baseRequirements,
          screenResolution: "2400 × 1632 pixels",
          aspectRatio: "25:17 (Landscape)",
          instructions: language === 'ar' 
            ? "شاشة كبيرة أفقية تتطلب دقة عالية جداً. يجب أن تكون العناصر المرئية كبيرة وواضحة لتكون مرئية من مسافات بعيدة."
            : "Large landscape screen requiring very high resolution. Visual elements should be large and clear to be visible from far distances.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 2400 × 1632 بكسل" : "Required resolution: 2400 × 1632 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 25:17 (أفقي)" : "Aspect ratio: 25:17 (Landscape)",
            language === 'ar' ? "شاشة كبيرة جداً - تحتاج عناصر مرئية كبيرة" : "Very large screen - requires large visual elements",
            language === 'ar' ? "دقة عالية مطلوبة للوضوح الأمثل" : "High resolution required for optimal clarity"
          ]
        };
      case "10.25m x 6m":
        return {
          ...baseRequirements,
          screenResolution: "984 × 576 pixels",
          aspectRatio: "10.25:6 (Landscape)",
          instructions: language === 'ar' 
            ? "شاشة أفقية متوسطة الحجم. مناسبة للمحتوى الأفقي مع تركيز على الوضوح والبساطة."
            : "Medium-sized landscape screen. Suitable for horizontal content with focus on clarity and simplicity.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 984 × 576 بكسل" : "Required resolution: 984 × 576 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 10.25:6" : "Aspect ratio: 10.25:6",
            language === 'ar' ? "مناسبة للمحتوى الأفقي" : "Suitable for horizontal content",
            language === 'ar' ? "ركز على البساطة والوضوح" : "Focus on simplicity and clarity"
          ]
        };
      case "8m x 6m":
        return {
          ...baseRequirements,
          screenResolution: "768 × 576 pixels",
          aspectRatio: "4:3 (Standard)",
          instructions: language === 'ar' 
            ? "نسبة عرض إلى ارتفاع قياسية 4:3. مثالية للمحتوى المربع تقريباً مع توازن جيد بين العرض والارتفاع."
            : "Standard 4:3 aspect ratio. Ideal for nearly square content with good balance between width and height.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 768 × 576 بكسل" : "Required resolution: 768 × 576 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 4:3 (قياسي)" : "Aspect ratio: 4:3 (Standard)",
            language === 'ar' ? "شكل مربع تقريباً" : "Nearly square format",
            language === 'ar' ? "توازن جيد للعناصر المرئية" : "Good balance for visual elements"
          ]
        };
      case "16m x 3m":
        return {
          ...baseRequirements,
          screenResolution: "1536 × 288 pixels",
          aspectRatio: "16:3 (Ultra Wide)",
          instructions: language === 'ar' 
            ? "شاشة عريضة جداً مناسبة للنصوص الطويلة أو المحتوى الأفقي الممتد. تجنب العناصر الرفيعة عمودياً."
            : "Ultra-wide screen suitable for long text or extended horizontal content. Avoid thin vertical elements.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 1536 × 288 بكسل" : "Required resolution: 1536 × 288 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 16:3 (عريض جداً)" : "Aspect ratio: 16:3 (Ultra Wide)",
            language === 'ar' ? "مثالية للنصوص الطويلة" : "Ideal for long text content",
            language === 'ar' ? "تجنب العناصر العمودية الرفيعة" : "Avoid thin vertical elements"
          ]
        };
      case "14m x 4m":
        return {
          ...baseRequirements,
          screenResolution: "1344 × 384 pixels",
          aspectRatio: "14:4 (Wide)",
          instructions: language === 'ar' 
            ? "شاشة عريضة مناسبة للمحتوى الأفقي مع ارتفاع محدود. ركز على العناصر الأفقية."
            : "Wide screen suitable for horizontal content with limited height. Focus on horizontal elements.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 1344 × 384 بكسل" : "Required resolution: 1344 × 384 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 14:4" : "Aspect ratio: 14:4",
            language === 'ar' ? "ارتفاع محدود - ركز أفقياً" : "Limited height - focus horizontally",
            language === 'ar' ? "مناسبة للشعارات العريضة" : "Suitable for wide logos"
          ]
        };
      case "20m x 3m":
        return {
          ...baseRequirements,
          screenResolution: "1920 × 288 pixels",
          aspectRatio: "20:3 (Ultra Wide)",
          instructions: language === 'ar' 
            ? "شاشة عريضة جداً مع ارتفاع محدود. مثالية للنصوص الطويلة، الشعارات الممتدة، أو المحتوى البانورامي."
            : "Ultra-wide screen with limited height. Ideal for long text, extended logos, or panoramic content.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 1920 × 288 بكسل" : "Required resolution: 1920 × 288 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 20:3" : "Aspect ratio: 20:3",
            language === 'ar' ? "عريض جداً مع ارتفاع قليل" : "Very wide with minimal height",
            language === 'ar' ? "مثالية للمحتوى البانورامي" : "Perfect for panoramic content"
          ]
        };
      case "15m x 2m":
        return {
          ...baseRequirements,
          screenResolution: "1440 × 192 pixels",
          aspectRatio: "15:2 (Banner)",
          instructions: language === 'ar' 
            ? "شاشة بتنسيق بانر طويل ورفيع. مناسبة للنصوص المختصرة، الشعارات، أو الرسائل البسيطة."
            : "Long and thin banner format screen. Suitable for short text, logos, or simple messages.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 1440 × 192 بكسل" : "Required resolution: 1440 × 192 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 15:2 (بانر)" : "Aspect ratio: 15:2 (Banner)",
            language === 'ar' ? "ارتفاع محدود جداً" : "Very limited height",
            language === 'ar' ? "مناسبة للرسائل المختصرة" : "Suitable for short messages"
          ]
        };
      case "15m x 3m":
        return {
          ...baseRequirements,
          screenResolution: "1440 × 288 pixels",
          aspectRatio: "5:1 (Wide Banner)",
          instructions: language === 'ar' 
            ? "شاشة بانر عريضة مع ارتفاع معتدل. توازن جيد بين العرض والارتفاع للمحتوى الأفقي."
            : "Wide banner screen with moderate height. Good balance between width and height for horizontal content.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 1440 × 288 بكسل" : "Required resolution: 1440 × 288 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 5:1" : "Aspect ratio: 5:1",
            language === 'ar' ? "توازن جيد للمحتوى الأفقي" : "Good balance for horizontal content",
            language === 'ar' ? "ارتفاع معتدل للنصوص" : "Moderate height for text"
          ]
        };
      case "18m x 3m":
        return {
          ...baseRequirements,
          screenResolution: "1728 × 288 pixels",
          aspectRatio: "6:1 (Extended Banner)",
          instructions: language === 'ar' 
            ? "شاشة بانر ممتدة مع عرض كبير. مناسبة للمحتوى الطويل مع ارتفاع محدود."
            : "Extended banner screen with large width. Suitable for long content with limited height.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 1728 × 288 بكسل" : "Required resolution: 1728 × 288 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 6:1" : "Aspect ratio: 6:1",
            language === 'ar' ? "بانر ممتد للمحتوى الطويل" : "Extended banner for long content",
            language === 'ar' ? "عرض كبير مع ارتفاع محدود" : "Large width with limited height"
          ]
        };
      case "4m x 7m":
        return {
          ...baseRequirements,
          screenResolution: "384 × 672 pixels",
          aspectRatio: "4:7 (Tall Portrait)",
          instructions: language === 'ar' 
            ? "شاشة عمودية طويلة ونحيفة. مناسبة للمحتوى العمودي مع تركيز على الارتفاع."
            : "Tall and narrow portrait screen. Suitable for vertical content with focus on height.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 384 × 672 بكسل" : "Required resolution: 384 × 672 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 4:7 (عمودي طويل)" : "Aspect ratio: 4:7 (Tall Portrait)",
            language === 'ar' ? "شاشة نحيفة وطويلة" : "Narrow and tall screen",
            language === 'ar' ? "ركز على المحتوى العمودي" : "Focus on vertical content"
          ]
        };
      case "4m x 3m":
        return {
          ...baseRequirements,
          screenResolution: "576 × 432 pixels",
          aspectRatio: "4:3 (Compact)",
          instructions: language === 'ar' 
            ? "شاشة مدمجة بنسبة عرض إلى ارتفاع قياسية. مناسبة للمحتوى المتوازن والمدمج."
            : "Compact screen with standard aspect ratio. Suitable for balanced and compact content.",
          technicalNotes: [
            language === 'ar' ? "الدقة المطلوبة: 576 × 432 بكسل" : "Required resolution: 576 × 432 pixels",
            language === 'ar' ? "نسبة العرض إلى الارتفاع: 4:3 (مدمج)" : "Aspect ratio: 4:3 (Compact)",
            language === 'ar' ? "حجم مدمج للمحتوى المتوازن" : "Compact size for balanced content",
            language === 'ar' ? "مناسبة للعرض القريب" : "Suitable for close viewing"
          ]
        };
      default:
        return {
          ...baseRequirements,
          screenResolution: "Custom resolution",
          aspectRatio: "Custom",
          instructions: language === 'ar' 
            ? "مواصفات شاشة مخصصة. يرجى التواصل مع فريق الدعم للحصول على التفاصيل الدقيقة."
            : "Custom screen specifications. Please contact support team for exact requirements.",
          technicalNotes: [
            language === 'ar' ? "مواصفات مخصصة" : "Custom specifications",
            language === 'ar' ? "اتصل بالدعم للتفاصيل" : "Contact support for details"
          ]
        };
    }
  };

  const handleAreaClick = (locationName: string) => {
    const input = document.getElementById(`custom-file-input-${locationName}`) as HTMLInputElement;
    input?.click();
  };

  // Group locations by screen dimensions
  const locationsByDimensions = locations.reduce((acc, location) => {
    const dimensions = location.details?.screenDimensions || "Unknown";
    if (!acc[dimensions]) {
      acc[dimensions] = [];
    }
    acc[dimensions].push(location);
    return acc;
  }, {} as Record<string, Location[]>);

  return (
    <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-orange-800 dark:text-orange-300">
          <AlertTriangle className="h-5 w-5" />
          {language === 'ar' ? 'مواد مخصصة مطلوبة' : 'Custom Materials Required'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-orange-800 dark:text-orange-300">
            {language === 'ar' 
              ? 'المواقع التي تحتوي على شاشات ذات أبعاد خاصة تتطلب مواد مخصصة بمواصفات محددة. يرجى اتباع التعليمات بدقة لضمان أفضل جودة عرض.'
              : 'Locations with special screen dimensions require custom materials with specific requirements. Please follow instructions carefully to ensure optimal display quality.'
            }
          </AlertDescription>
        </Alert>

        {/* Render sections for each screen dimension */}
        {Object.entries(locationsByDimensions).map(([dimensions, dimensionLocations]) => {
          const requirements = getDetailedRequirements(dimensions);
          
          return (
            <div key={dimensions} className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-orange-800">
                  {dimensions} {language === 'ar' ? 'الشاشات' : 'Screens'}
                </h3>
                <Badge variant="secondary">{dimensionLocations.length} location{dimensionLocations.length > 1 ? 's' : ''}</Badge>
              </div>

              {/* Detailed Requirements Section */}
              <div className="bg-card dark:bg-card p-6 rounded-lg border border-border shadow-sm">
                <h4 className="font-semibold mb-4 text-orange-700 dark:text-orange-400 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {language === 'ar' ? 'المتطلبات التقنية التفصيلية:' : 'Detailed Technical Requirements:'}
                </h4>
                
                {/* Technical Specifications Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      <Video className="inline h-3 w-3 mr-1" />
                      {language === 'ar' ? 'تنسيق الفيديو' : 'Video Format'}
                    </span>
                    <Badge variant="outline" className="justify-center">
                      {requirements.videoFormat}
                    </Badge>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      <Image className="inline h-3 w-3 mr-1" />
                      {language === 'ar' ? 'تنسيق الصورة' : 'Image Format'}
                    </span>
                    <Badge variant="outline" className="justify-center">
                      {requirements.imageFormat}
                    </Badge>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      {language === 'ar' ? 'دقة الشاشة' : 'Screen Resolution'}
                    </span>
                    <Badge variant="outline" className="justify-center">
                      {requirements.screenResolution}
                    </Badge>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground mb-1">
                      {language === 'ar' ? 'نسبة العرض إلى الارتفاع' : 'Aspect Ratio'}
                    </span>
                    <Badge variant="outline" className="justify-center">
                      {requirements.aspectRatio}
                    </Badge>
                  </div>
                </div>

                {/* File Size Limits */}
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h5 className="font-medium text-blue-800 dark:text-blue-300 mb-2">
                    {language === 'ar' ? 'حدود حجم الملف:' : 'File Size Limits:'}
                  </h5>
                  <div className="flex gap-4 text-sm">
                    <span className="text-blue-700 dark:text-blue-400">
                      <Image className="inline h-3 w-3 mr-1" />
                      {language === 'ar' ? 'الصور:' : 'Images:'} {requirements.maxFileSize.image}
                    </span>
                    <span className="text-blue-700 dark:text-blue-400">
                      <Video className="inline h-3 w-3 mr-1" />
                      {language === 'ar' ? 'الفيديو:' : 'Videos:'} {requirements.maxFileSize.video}
                    </span>
                  </div>
                </div>

                {/* Design Instructions */}
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                  <h5 className="font-medium text-green-800 dark:text-green-300 mb-2">
                    {language === 'ar' ? 'تعليمات التصميم:' : 'Design Instructions:'}
                  </h5>
                  <p className="text-green-700 dark:text-green-400 text-sm">
                    {requirements.instructions}
                  </p>
                </div>

                {/* Technical Notes */}
                <div className="p-3 bg-muted/50 dark:bg-muted/20 rounded-lg border border-border">
                  <h5 className="font-medium text-foreground mb-2">
                    {language === 'ar' ? 'ملاحظات تقنية:' : 'Technical Notes:'}
                  </h5>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {requirements.technicalNotes.map((note, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-muted-foreground mt-1">•</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Upload areas for each location in this dimension */}
              <div className="space-y-4">
                {dimensionLocations.map((location) => {
                  const locationMaterials = locationCreatives[location.name] || [];
                  
                  return (
                    <div key={location.name} className="border border-border rounded-lg p-4 bg-card dark:bg-card">
                      <h5 className="font-medium mb-3 text-foreground">
                        {location.name}
                        <Badge variant="secondary" className="ml-2">
                          {dimensions}
                        </Badge>
                      </h5>

                      {/* Day-based upload with scheduling */}
                      <DayBasedUpload
                        locationName={location.name}
                        creatives={locationMaterials}
                        onFileChange={onLocationFileChange}
                        onRemoveCreative={onRemoveLocationCreative}
                        onUpdateCreativeDays={onUpdateLocationCreativeDays}
                        isUploading={isUploading}
                      />

                      {/* Validation status */}
                      {locationMaterials.length === 0 && (
                        <Alert className="mt-3 border-red-200 bg-red-50">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription className="text-red-800">
                            {language === 'ar' 
                              ? 'مطلوب: يرجى رفع مواد مخصصة لهذا الموقع وفقاً للمواصفات أعلاه'
                              : 'Required: Please upload custom materials for this location according to specifications above'
                            }
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {isUploading && (
          <div className="text-center py-4">
            <div className="inline-flex flex-col items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-orange-700 bg-orange-100">
              <div className="flex items-center mb-2">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-orange-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {language === 'ar' 
                  ? uploadProgress < 30 ? 'جاري التجهيز...' : uploadProgress < 90 ? `جاري الرفع... ${uploadProgress}%` : 'جاري الإنهاء...'
                  : uploadProgress < 30 ? 'Preparing...' : uploadProgress < 90 ? `Uploading... ${uploadProgress}%` : 'Finalizing...'
                }
              </div>
              <div className="w-32 h-2 bg-orange-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-orange-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomMaterialUpload;
